import { DefaultLoader, LoaderContainer } from 'components/Loader';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  setPage,
  setSearch,
  setTags,
  setTake,
} from 'store/actions/admin/documentationFileLists';
import { StoreState } from 'store/reducers';
import useSWR from 'swr';
import DocumentationFile from 'types/DocumentationFile';
import Tags from 'types/Tags';
import { Action } from 'types/typeguards/reducerActions';
import getTagsQueryParams from 'utils/tagQueryParam';
import DocumentationFileFilters from './index-components/DocumentationFileFilters';
import DocumentationFileList from './index-components/DocumentationFileList';
import Head from './index-components/Head';

export const tagColumnNames = ['role'] as const;
export type TagColumnNames = typeof tagColumnNames[number];

type RetrievedData = {
  files: DocumentationFile[] | undefined;
  pages: number | undefined;
};

type Props = {
  page: number;
  setPage: (page: number) => void;
  search: string;
  setSearch: (search: string) => void;
  tags: Tags<TagColumnNames>;
  setTags: (tag: Tags<TagColumnNames>) => void;
  take: number;
  setTake: (take: number) => void;
};

type ContextType = RetrievedData & {
  loading: boolean;
} & Props;
export const DocumentationFilesContext = React.createContext({} as ContextType);
const { Provider } = DocumentationFilesContext;

const DocumentationFiles: FC<Props> = ({
  children,
  page,
  setPage,
  search,
  setSearch,
  tags,
  setTags,
  take,
  setTake,
}) => {
  const { data, error } = useSWR<RetrievedData>(
    `/files/documentation-files/\
		?page=${page}&take=${take}\
${search ? `&search=${search}` : ''}\
${getTagsQueryParams<TagColumnNames>(tags, ['role'])}\
`,
  );

  const [retrievedData, setRetrievedData] = useState<RetrievedData>({
    files: undefined,
    pages: undefined,
  });
  useEffect(() => {
    if (data) {
      setRetrievedData(data);
    }
  }, [data]);

  // Reset page to 0 if the stored page is out of range
  useEffect(() => {
    if (data?.pages && data?.pages <= page) {
      setPage(0);
    }
  }, [data, page, setPage]);

  const value = useMemo(
    () => ({
      ...retrievedData,
      page,
      setPage,
      search,
      setSearch,
      loading: !data,
      tags,
      setTags,
      take,
      setTake,
    }),
    [
      page,
      setPage,
      search,
      setSearch,
      retrievedData,
      data,
      tags,
      setTags,
      take,
      setTake,
    ],
  );

  return retrievedData.files || error ? (
    <Provider value={value}>{children}</Provider>
  ) : (
    <LoaderContainer>
      <DefaultLoader size={1.5} />
    </LoaderContainer>
  );
};

const Usage: FC<Props> = (props) => {
  return (
    <DocumentationFiles {...props}>
      <Head />
      <DocumentationFileFilters />
      <DocumentationFileList />
    </DocumentationFiles>
  );
};

const mapStateToProps = (state: StoreState) => {
  return {
    page: state.admin.documentationFileLists.main.page,
    search: state.admin.documentationFileLists.main.search,
    tags: state.admin.documentationFileLists.main.tags,
    take: state.admin.documentationFileLists.main.take,
  };
};

const mapDispatchToProps = (dispatch: (action: Action) => void) => {
  return {
    setPage: (page: number) => dispatch(setPage('main', page)),
    setSearch: (search: string) => dispatch(setSearch('main', search)),
    setTags: (tags: Tags<TagColumnNames>) =>
      dispatch(setTags<TagColumnNames>('main', tags)),
    setTake: (take: number) => dispatch(setTake('main', take)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Usage);
